import { useState, useEffect } from 'react'
import { X, Plus, Trash2, ChevronDown } from 'lucide-react'
import {
  useCreateChekiForm,
  useUpdateChekiForm,
  useTenantChekiGroupSetting,
  useTenantChekiDefaultPrices,
  type ChekiForm,
  type CreateChekiFormInput,
} from '@/hooks/use-cheki'
import { useEvents, useEventLineup } from '@/hooks/use-events'
import { useMembers } from '@/hooks/use-members'
import { useChekiFormTemplates, type FieldDef } from '@/hooks/use-cheki-templates'
import FieldBuilder from './field-builder'

function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return ''
  return iso.slice(0, 16)
}

interface MemberQuotaRow {
  member_id: string
  quota: number      // PO quota (min 1)
  quota_ots: number  // OTS quota, 0 = unlimited
}

interface FormState {
  title: string
  event_id: string
  opens_at: string
  closes_at: string
  quota_type: 'group' | 'per_member'
  total_quota: string       // PO quota for group mode, '' = unlimited
  total_quota_ots: string   // OTS quota for group mode, '' / '0' = unlimited
  price_idr: number
  price_ots_idr: string      // '' = null
  group_price_idr: string    // '' = null (not offered)
  group_price_ots_idr: string // '' = null
  group_cheki_quota: string     // PO quota for group cheki, '' = unlimited
  group_cheki_quota_ots: string // OTS quota for group cheki, '' / '0' = unlimited
  status: 'draft' | 'open' | 'closed'
  members: MemberQuotaRow[]
  template_id: string | null
  custom_fields: FieldDef[]
}

const EMPTY_FORM: FormState = {
  title: '',
  event_id: '',
  opens_at: '',
  closes_at: '',
  quota_type: 'group',
  total_quota: '',
  total_quota_ots: '',
  price_idr: 0,
  price_ots_idr: '',
  group_price_idr: '',
  group_price_ots_idr: '',
  group_cheki_quota: '',
  group_cheki_quota_ots: '',
  status: 'draft',
  members: [],
  template_id: null,
  custom_fields: [],
}

interface ChekiFormModalProps {
  open: boolean
  onClose: () => void
  form?: ChekiForm
}

const STATUS_LABELS: Record<'draft' | 'open' | 'closed', string> = {
  draft: 'Draft (tidak tampil publik)',
  open: 'Buka Pendaftaran',
  closed: 'Tutup Pendaftaran',
}

export default function ChekiFormModal({ open, onClose, form }: ChekiFormModalProps) {
  const isEdit = !!form
  const create = useCreateChekiForm()
  const update = useUpdateChekiForm()
  const { query: chekiGroupQuery } = useTenantChekiGroupSetting()
  const { query: defaultPricesQuery } = useTenantChekiDefaultPrices()
  const hasChekiGroup = chekiGroupQuery.data?.has_cheki_group ?? false
  const { data: events = [] } = useEvents()
  const { data: idolMembers = [] } = useMembers()
  const { data: templates = [] } = useChekiFormTemplates()

  const [state, setState] = useState<FormState>(EMPTY_FORM)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('')
  const [additionalOpen, setAdditionalOpen] = useState(false)

  const { data: eventLineup = [] } = useEventLineup(state.event_id || undefined)

  const activeMembers = idolMembers.filter((m) => m.is_active)
  const availableMembers = eventLineup.length > 0
    ? activeMembers.filter((m) => eventLineup.some((em) => em.id === m.id))
    : activeMembers

  useEffect(() => {
    if (!open) return
    if (form) {
      setState({
        title: form.title,
        event_id: form.event_id,
        opens_at: toLocalInput(form.opens_at),
        closes_at: toLocalInput(form.closes_at),
        quota_type: form.quota_type,
        total_quota: form.total_quota != null ? String(form.total_quota) : '',
        total_quota_ots: form.total_quota_ots ? String(form.total_quota_ots) : '',
        price_idr: form.price_idr,
        price_ots_idr: form.price_ots_idr != null ? String(form.price_ots_idr) : '',
        group_price_idr: form.group_price_idr != null ? String(form.group_price_idr) : '',
        group_price_ots_idr: form.group_price_ots_idr != null ? String(form.group_price_ots_idr) : '',
        group_cheki_quota: form.group_cheki_quota != null ? String(form.group_cheki_quota) : '',
        group_cheki_quota_ots: form.group_cheki_quota_ots ? String(form.group_cheki_quota_ots) : '',
        status: form.status,
        members: (form.cheki_form_members ?? []).map((m) => ({
          member_id: m.member_id,
          quota: m.quota,
          quota_ots: m.quota_ots ?? 0,
        })),
        template_id: form.template_id,
        custom_fields: form.custom_fields ?? [],
      })
      setSelectedTemplateId(form.template_id ?? '')
    } else {
      const d = defaultPricesQuery.data
      setState({
        ...EMPTY_FORM,
        price_idr: d?.default_price_idr ?? 0,
        price_ots_idr: d?.default_price_ots_idr != null ? String(d.default_price_ots_idr) : '',
        group_price_idr: d?.default_group_price_idr != null ? String(d.default_group_price_idr) : '',
        group_price_ots_idr: d?.default_group_price_ots_idr != null ? String(d.default_group_price_ots_idr) : '',
      })
      setSelectedTemplateId('')
    }
    create.reset()
    update.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form?.id])

  // Auto-populate + sync member rows whenever per_member mode is active.
  // Triggers on: quota_type switch, event change, lineup load, or member list update.
  // - Adds all lineup members (or all active members if no lineup) with default quotas.
  // - Preserves existing quota values for members already in the list.
  // - Removes members that are no longer in the available lineup.
  useEffect(() => {
    if (state.quota_type !== 'per_member') return
    const lineup = eventLineup.length > 0
      ? idolMembers.filter((m) => m.is_active && eventLineup.some((em) => em.id === m.id))
      : idolMembers.filter((m) => m.is_active)
    if (lineup.length === 0) return
    setState((s) => ({
      ...s,
      members: lineup.map((m) => {
        const existing = s.members.find((r) => r.member_id === m.id)
        return existing ?? { member_id: m.id, quota: 1, quota_ots: 0 }
      }),
    }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.quota_type, state.event_id, eventLineup, idolMembers])

  const isPending = create.isPending || update.isPending
  const mutationError = create.error ?? update.error

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setState((s) => ({ ...s, [key]: value }))
  }

  function applyTemplate() {
    const tpl = templates.find((t) => t.id === selectedTemplateId)
    if (!tpl) return
    setState((s) => ({
      ...s,
      template_id: tpl.id,
      custom_fields: tpl.fields.map((f) => ({ ...f })),
    }))
  }

  function addMemberRow() {
    const available = availableMembers.filter(
      (m) => !state.members.some((r) => r.member_id === m.id)
    )
    if (!available.length) return
    setState((s) => ({
      ...s,
      members: [...s.members, { member_id: available[0].id, quota: 1, quota_ots: 0 }],
    }))
  }

  function removeMemberRow(memberId: string) {
    setState((s) => ({ ...s, members: s.members.filter((r) => r.member_id !== memberId) }))
  }

  function updateMemberRow(memberId: string, field: keyof MemberQuotaRow, value: string | number) {
    setState((s) => ({
      ...s,
      members: s.members.map((r) =>
        r.member_id === memberId ? { ...r, [field]: value } : r
      ),
    }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const payload: CreateChekiFormInput = {
      event_id: state.event_id,
      title: state.title.trim(),
      opens_at: new Date(state.opens_at).toISOString(),
      closes_at: new Date(state.closes_at).toISOString(),
      quota_type: state.quota_type,
      total_quota: state.quota_type === 'group' && state.total_quota
        ? Number(state.total_quota)
        : null,
      total_quota_ots: state.quota_type === 'group' && state.total_quota_ots
        ? Number(state.total_quota_ots)
        : 0,
      price_idr: state.price_idr,
      price_ots_idr: state.price_ots_idr ? Number(state.price_ots_idr) : null,
      group_price_idr: hasChekiGroup && state.group_price_idr ? Number(state.group_price_idr) : null,
      group_price_ots_idr: hasChekiGroup && state.group_price_ots_idr ? Number(state.group_price_ots_idr) : null,
      group_cheki_quota: hasChekiGroup && state.group_cheki_quota ? Number(state.group_cheki_quota) : null,
      group_cheki_quota_ots: hasChekiGroup && state.group_cheki_quota_ots
        ? Number(state.group_cheki_quota_ots)
        : 0,
      status: state.status,
      template_id: state.template_id,
      custom_fields: state.custom_fields,
      members: state.quota_type === 'per_member' ? state.members : [],
    }

    if (isEdit && form) {
      update.mutate({ id: form.id, ...payload }, { onSuccess: onClose })
    } else {
      create.mutate(payload, { onSuccess: onClose })
    }
  }

  if (!open) return null

  const usedMemberIds = new Set(state.members.map((r) => r.member_id))

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 w-full max-w-xl rounded-xl border border-border bg-card shadow-xl">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="font-semibold">{isEdit ? 'Edit Form Cheki' : 'Buat Form Cheki'}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4 max-h-[75vh] overflow-y-auto">

          {/* Title */}
          <div className="space-y-1">
            <label className="text-sm font-medium">Judul Form *</label>
            <input
              required
              value={state.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder='Contoh: "Cheki Sesi 1 — SAKURA FES"'
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
            />
          </div>

          {/* Event */}
          <div className="space-y-1">
            <label className="text-sm font-medium">Event *</label>
            <select
              required
              value={state.event_id}
              onChange={(e) => set('event_id', e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
            >
              <option value="">— Pilih event —</option>
              {events.map((ev) => (
                <option key={ev.id} value={ev.id}>{ev.title}</option>
              ))}
            </select>
          </div>

          {/* Open / Close window */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Pendaftaran Dibuka *</label>
              <input
                type="datetime-local"
                required
                value={state.opens_at}
                onChange={(e) => set('opens_at', e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Pendaftaran Ditutup *</label>
              <input
                type="datetime-local"
                required
                value={state.closes_at}
                onChange={(e) => set('closes_at', e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
              />
            </div>
          </div>

          {/* Price */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Harga Pre-order (Rp)</label>
              <input
                type="number"
                min={0}
                step={1000}
                value={state.price_idr}
                onChange={(e) => set('price_idr', Number(e.target.value))}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
                placeholder="75000"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">
                Harga OTS (Rp)
                <span className="ml-1 font-normal text-muted-foreground">(opsional)</span>
              </label>
              <input
                type="number"
                min={0}
                step={1000}
                value={state.price_ots_idr}
                onChange={(e) => set('price_ots_idr', e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
                placeholder="100000"
              />
            </div>
          </div>

          {/* Status */}
          <div className="space-y-1">
            <label className="text-sm font-medium">Status</label>
            <select
              value={state.status}
              onChange={(e) => set('status', e.target.value as FormState['status'])}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
            >
              {(Object.keys(STATUS_LABELS) as Array<keyof typeof STATUS_LABELS>).map((s) => (
                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
              ))}
            </select>
          </div>

          {/* Quota section */}
          <fieldset className="rounded-lg border border-border p-4 space-y-3">
            <legend className="text-sm font-medium px-1">Kuota Cheki</legend>

            <div className="flex gap-4">
              {(['group', 'per_member'] as const).map((type) => (
                <label key={type} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="quota_type"
                    value={type}
                    checked={state.quota_type === type}
                    onChange={() => set('quota_type', type)}
                    className="accent-primary"
                  />
                  <span className="text-sm">
                    {type === 'group' ? 'Kuota Bersama (Total)' : 'Per Member (Individual)'}
                  </span>
                </label>
              ))}
            </div>

            {state.quota_type === 'group' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-sm font-medium text-muted-foreground">
                    Kuota PO
                    <span className="ml-1 font-normal">(kosongkan = ∞)</span>
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={state.total_quota}
                    onChange={(e) => set('total_quota', e.target.value)}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
                    placeholder="50"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-muted-foreground">
                    Kuota OTS
                    <span className="ml-1 font-normal">(0 = ∞)</span>
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={state.total_quota_ots}
                    onChange={(e) => set('total_quota_ots', e.target.value)}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
                    placeholder="0"
                  />
                </div>
              </div>
            )}

            {state.quota_type === 'per_member' && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Setiap member punya kuota PO dan OTS independen.
                  {availableMembers.length > 0 && ' Member dari lineup event sudah otomatis ditambahkan.'}
                </p>
                {state.members.length === 0 && availableMembers.length === 0 && (
                  <p className="text-sm text-muted-foreground italic">Belum ada member aktif.</p>
                )}

                {/* Column headers */}
                {state.members.length > 0 && (
                  <div className="flex items-center gap-2 pb-0.5">
                    <span className="flex-1 text-xs text-muted-foreground">Member</span>
                    <span className="w-20 text-xs text-muted-foreground text-center">PO</span>
                    <span className="w-20 text-xs text-muted-foreground text-center">
                      OTS <span className="text-muted-foreground/60">(0=∞)</span>
                    </span>
                    <span className="w-7" />
                  </div>
                )}

                {state.members.map((row) => {
                  const available = availableMembers.filter(
                    (m) => m.id === row.member_id || !usedMemberIds.has(m.id)
                  )
                  return (
                    <div key={row.member_id} className="flex items-center gap-2">
                      <select
                        value={row.member_id}
                        onChange={(e) => updateMemberRow(row.member_id, 'member_id', e.target.value)}
                        className="flex-1 rounded-md border border-border bg-background px-3 py-1.5 text-sm focus:border-primary focus:outline-none"
                      >
                        {available.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.stage_name ?? m.name}
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        min={1}
                        value={row.quota}
                        onChange={(e) => updateMemberRow(row.member_id, 'quota', Number(e.target.value))}
                        className="w-20 rounded-md border border-border bg-background px-2 py-1.5 text-sm text-center focus:border-primary focus:outline-none"
                        placeholder="PO"
                      />
                      <input
                        type="number"
                        min={0}
                        value={row.quota_ots}
                        onChange={(e) => updateMemberRow(row.member_id, 'quota_ots', Number(e.target.value))}
                        className="w-20 rounded-md border border-border bg-background px-2 py-1.5 text-sm text-center focus:border-primary focus:outline-none"
                        placeholder="OTS"
                      />
                      <button
                        type="button"
                        onClick={() => removeMemberRow(row.member_id)}
                        className="w-7 p-1.5 text-destructive hover:bg-destructive/10 rounded shrink-0"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )
                })}

                {state.members.length < availableMembers.length && (
                  <button
                    type="button"
                    onClick={addMemberRow}
                    className="flex items-center gap-1.5 text-sm text-primary hover:underline"
                  >
                    <Plus className="h-4 w-4" />
                    Tambahkan Member Lagi
                  </button>
                )}
              </div>
            )}
          </fieldset>

          {/* Harga Cheki Grup */}
          {hasChekiGroup && (
            <fieldset className="rounded-lg border border-border p-4 space-y-3">
              <legend className="text-sm font-medium px-1">Harga & Kuota Cheki Grup</legend>
              <p className="text-xs text-muted-foreground">
                Kosongkan semua field ini jika form ini tidak menawarkan cheki grup (foto bersama seluruh grup).
              </p>

              {/* Harga */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Harga Grup (PO) (Rp)</label>
                  <input
                    type="number"
                    min={0}
                    step={1000}
                    value={state.group_price_idr}
                    onChange={(e) => set('group_price_idr', e.target.value)}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
                    placeholder="150000"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">
                    Harga Grup (OTS) (Rp)
                    <span className="ml-1 font-normal text-muted-foreground">(opsional)</span>
                  </label>
                  <input
                    type="number"
                    min={0}
                    step={1000}
                    value={state.group_price_ots_idr}
                    onChange={(e) => set('group_price_ots_idr', e.target.value)}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
                    placeholder="200000"
                  />
                </div>
              </div>

              {/* Kuota Grup PO + OTS */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-sm font-medium">
                    Kuota Grup PO
                    <span className="ml-1 font-normal text-muted-foreground">(kosongkan = ∞)</span>
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={state.group_cheki_quota}
                    onChange={(e) => set('group_cheki_quota', e.target.value)}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
                    placeholder="20"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">
                    Kuota Grup OTS
                    <span className="ml-1 font-normal text-muted-foreground">(0 = ∞)</span>
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={state.group_cheki_quota_ots}
                    onChange={(e) => set('group_cheki_quota_ots', e.target.value)}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
                    placeholder="0"
                  />
                </div>
              </div>
            </fieldset>
          )}

          {/* Additional settings — collapsible */}
          <div className="rounded-lg border border-border">
            <button
              type="button"
              onClick={() => setAdditionalOpen((v) => !v)}
              className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium hover:bg-accent/50 rounded-lg"
            >
              <span>Pengaturan Tambahan</span>
              <ChevronDown
                className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${additionalOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {additionalOpen && (
              <div className="px-4 pb-4 space-y-4 border-t border-border pt-4">
                {/* Template picker */}
                {templates.length > 0 && (
                  <div className="space-y-1">
                    <label className="text-sm font-medium">Template Field Tambahan</label>
                    <div className="flex gap-2">
                      <select
                        value={selectedTemplateId}
                        onChange={(e) => setSelectedTemplateId(e.target.value)}
                        className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
                      >
                        <option value="">— Pilih template (opsional) —</option>
                        {templates.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name} ({t.fields.length} field)
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        disabled={!selectedTemplateId}
                        onClick={applyTemplate}
                        className="rounded-md border border-border px-3 py-2 text-sm hover:bg-accent disabled:opacity-40"
                      >
                        Terapkan
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Menerapkan template akan mengganti field tambahan yang ada.
                    </p>
                  </div>
                )}

                {/* Custom fields builder */}
                <fieldset className="rounded-lg border border-border p-4 space-y-3">
                  <legend className="text-sm font-medium px-1">Field Tambahan</legend>
                  <p className="text-xs text-muted-foreground">
                    Field ekstra yang akan ditampilkan di form pendaftaran fan.
                  </p>
                  <FieldBuilder
                    fields={state.custom_fields}
                    onChange={(f) => set('custom_fields', f)}
                  />
                </fieldset>
              </div>
            )}
          </div>

          {mutationError && (
            <p className="text-sm text-destructive">
              {(mutationError as Error).message ?? 'Terjadi kesalahan. Coba lagi.'}
            </p>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border px-4 py-2 text-sm hover:bg-accent"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isPending ? 'Menyimpan...' : isEdit ? 'Simpan Perubahan' : 'Buat Form'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
